const U = (id, extra = "") =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=1200&q=80${extra}`;

export const IMAGES = {
  interior:
    "https://images.unsplash.com/photo-1585418694458-dc80a5c20294?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBib3V0aXF1ZSUyMGhvdGVsJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzg2NTUwNzIyfDA&ixlib=rb-4.1.0&q=85&w=1100",
  suite:
    "https://images.unsplash.com/photo-1590381105924-c72589b9ef3f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBib3V0aXF1ZSUyMGhvdGVsJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzg2NTUwNzIyfDA&ixlib=rb-4.1.0&q=85&w=1100",
  london: U("photo-1513635269975-59663e0ac1ad"),
  londonStreet: U("photo-1543832923-44667a44c804"),
  restaurant: U("photo-1414235077428-338989a2e8c0"),
  service: U("photo-1559339352-11d035aa65de"),
  lobby: U("photo-1625244724120-1fd1d34d00f6"),
  lobbyAlt: U("photo-1677129667171-92abd8740fa3"),
  dining: U("photo-1570560258879-af7f8e1447ac"),
};

export const CONTACT = {
  email: "patilrushi0206@gmail.com",
  linkedin: "https://www.linkedin.com/in/rushikesh-patil-5887a4213",
  location: "United Kingdom",
  cv: "/Rushikesh-Patil-CV.pdf",
};

export const PROJECTS = [
  {
    slug: "london-conversion-feasibility",
    num: "01",
    category: "Hotel Investment & Feasibility",
    label: "Academic Feasibility Case Study — MSc Strategic Hotel Management",
    title: "From Car Park to Hotel: London Conversion Feasibility",
    cardTitle: "From Car Park to Hotel: London Conversion Feasibility",
    cardSub: "Tower Bridge conversion feasibility",
    questionLine: "Should this London asset become a hotel?",
    home: {
      question: "Should this London asset become a hotel?",
      context: "Car Park Conversion Feasibility",
      tags: ["Market Analysis", "Positioning", "Hotel Economics"],
      outcome: "100-key upper-midscale select-service concept.",
      cta: "Explore the Decision",
    },
    brief: {
      question:
        "Could a central London car park sustain a 100-key upper-midscale hotel — and under what ownership structure?",
      evidence:
        "Demand-generator mapping, a benchmarked competitor set, concept and brand positioning, HMA vs franchise evaluation, and a five-year performance model with sensitivity scenarios.",
      insight:
        "The market could absorb a select-service product — but only at a disciplined price point and cost structure.",
      decision:
        "Proceed as a branded select-service conversion on a franchise-style structure, underwritten on conservative, sensitivity-tested assumptions.",
    },
    insight:
      "The challenge was not simply designing a hotel — it was determining what hotel the market could realistically support.",
    tags: ["Market Analysis", "Feasibility", "Financial Modelling"],
    metric: { value: "100", label: "proposed keys, Zone 1 London" },
    image: IMAGES.london,
    imageAlt: "Tower Bridge, London — the market context for the conversion feasibility",
    heroImage: U("photo-1513635269975-59663e0ac1ad", "&h=900"),
    heroAlt: "Tower Bridge and the City of London — the corridor the site sits within",
    gallery: [
      { src: IMAGES.londonStreet, alt: "London street context near the site", caption: "Site context — illustrative" },
      { src: U("photo-1585418694458-dc80a5c20294", "&h=700"), alt: "Upper-midscale select-service hotel interior", caption: "Select-service concept — illustrative" },
    ],
    context:
      "A non-operating investor held the Q-Park Tower Bridge car park — unbuilt potential in a supply-constrained London Bridge corridor. The question was not architectural but commercial: what hotel, if any, could this site realistically support?",
    approach:
      "Market first, product second, numbers last. The analysis moved through five stages — site, market, positioning, performance, decision — so the concept was shaped by evidence rather than preference.",
    analysis: [
      "Location and demand-generator mapping across business, leisure, transport and events",
      "Competitor set benchmarking — occupancy, ADR and price-value position",
      "Concept and brand positioning for a 100-key select-service product",
      "HMA vs franchise structures — control, distribution and cost trade-offs",
      "Five-year projections: ADR, occupancy, RevPAR and GOP, base and downside",
      "Sensitivity analysis on the assumptions the investment case depended on most",
    ],
    insightText:
      "London demand is deep but not forgiving. The comp set rewards distribution strength and cost discipline — a branded select-service product held a defensible position where heavier concepts would not.",
    recommendation: [
      "Proceed as a branded upper-midscale select-service conversion",
      "Structure as franchise-style — brand distribution without a heavy HMA",
      "Underwrite on conservative, sensitivity-tested occupancy and ADR",
    ],
    demonstrates: ["Market Analysis", "Concept Development", "Hotel Economics", "Investment Thinking"],
    nextSteps: [
      "Validate conversion CAPEX and buildability",
      "Test additional operator and brand structures",
      "Extend downside scenario analysis",
    ],
    takeaway:
      "Feasibility connects market evidence, product positioning, operating assumptions and financial performance into one investment decision.",
    stages: [
      {
        id: "site",
        step: "01",
        name: "SITE",
        question: "What could this asset become?",
        body: "A surface car park minutes from Tower Bridge: unbuilt potential in a supply-constrained corridor. The site analysis weighed plot capacity, access, visibility — and the scarcity of new select-service supply nearby.",
        chips: ["Zone 1 location", "100-key capacity", "Non-operating investor"],
        siteFacts: [
          { k: "Existing asset", v: "Surface car park" },
          { k: "Proposed", v: "100-key hotel" },
          { k: "Investor", v: "Non-operating" },
        ],
      },
      {
        id: "market",
        step: "02",
        name: "MARKET",
        question: "Would the location support the hotel?",
        body: "Demand mapped generator by generator, then benchmarked against a branded competitor set on occupancy, ADR and price-value position.",
        chips: ["Business + leisure mix", "Comp set benchmarked", "Demand calendar mapped"],
        marketMap: true,
      },
      {
        id: "positioning",
        step: "03",
        name: "POSITIONING",
        question: "What product belongs here?",
        body: "The price-value ladder showed a workable gap: branded, upper-midscale select service. Brand options were weighed for distribution strength and cost; a franchise-style structure preserved investor flexibility without losing brand reach.",
        chips: ["Price-value gap identified", "Brand options evaluated", "HMA vs franchise compared"],
        matrix: true,
      },
      {
        id: "performance",
        step: "04",
        name: "PERFORMANCE",
        question: "How could the hotel perform?",
        body: "A five-year model translated the market evidence into operations: occupancy ramp to stabilisation, ADR against the comp-set ceiling, RevPAR trajectory and GOP under select-service cost ratios — in base and downside scenarios.",
        chips: ["5-year horizon", "2 scenarios", "ADR · Occ · RevPAR · GOP"],
        scenarios: [
          {
            name: "Base case",
            lines: [
              "Stabilised occupancy in line with the comp set",
              "ADR held under the market rate ceiling",
              "GOP sustained by select-service cost ratios",
            ],
          },
          {
            name: "Downside case",
            lines: [
              "Slower ramp and softer ADR tested",
              "Break-even occupancy identified",
              "Structure remains viable at reduced trading",
            ],
          },
        ],
        scenarioNote: "Quantified outputs withheld here pending validated figures from the source model.",
      },
      {
        id: "decision",
        step: "05",
        name: "DECISION",
        question: "Would the concept be commercially defensible?",
        body: "",
        chips: [],
        decisionGrid: [
          { k: "Market Fit", v: "Deep, year-round demand against a competitive but readable comp set" },
          { k: "Product", v: "Branded upper-midscale select-service, 100 keys" },
          { k: "Operating Structure", v: "Franchise-style — brand distribution, investor flexibility" },
          { k: "Key Risk", v: "Construction cost inflation and new supply entering the comp set" },
        ],
        finalLine: "Yes — under discipline: branded select-service, franchise structure, conservative underwriting.",
      },
    ],
  },
  {
    slug: "total-revenue-strategy",
    num: "02",
    category: "Revenue & Commercial Analytics",
    label: "Strategic Analysis — MSc Strategic Hotel Management",
    title: "Beyond RevPAR: Building a Total Revenue Strategy",
    cardTitle: "Beyond RevPAR: Building a Total Revenue Strategy",
    cardSub: "250-room four-star hotel",
    questionLine: "How can a hotel optimise revenue beyond rooms?",
    home: {
      question: "How can a hotel optimise revenue beyond rooms?",
      context: "250-room four-star hotel",
      tags: ["RevPAR", "TRevPAR", "GOPPAR", "Forecasting"],
      outcome: "Rooms were only part of the commercial picture.",
      cta: "Explore the Strategy",
    },
    brief: {
      question:
        "Where does a full-service hotel actually make its money — and what would a total revenue strategy change?",
      evidence:
        "A baseline audit of a fictional 250-room four-star hotel across Rooms, F&B, Events and Spa: occupancy, ADR, RevPAR, TRevPAR, GOPPAR, utilisation and variable costs.",
      insight:
        "Rooms carried the dashboard; F&B and events carried the margin volatility. Decisions were being made on a third of the picture.",
      decision:
        "Move from rooms-first revenue management to a total-revenue framework governed by TRevPAR and GOPPAR.",
    },
    insight:
      "Rooms carried the KPI dashboard, but F&B and events carried the margin volatility.",
    tags: ["Revenue Management", "TRevPAR & GOPPAR", "Commercial Strategy"],
    metric: { value: "£161.88", label: "TRevPAR vs £102.24 RevPAR" },
    image: IMAGES.suite,
    imageAlt: "Premium full-service hotel suite — one of several revenue environments",
    heroImage: U("photo-1590381105924-c72589b9ef3f", "&h=900"),
    heroAlt: "Full-service hotel suite — the rooms business is only part of the picture",
    gallery: [
      { src: IMAGES.lobby, alt: "Full-service hotel lobby and public areas", caption: "Full-service environment — illustrative" },
      { src: IMAGES.dining, alt: "Hotel dining environment representing non-rooms revenue", caption: "Non-rooms revenue — illustrative" },
    ],
    context:
      "A fictional 250-room, four-star Guildford hotel running Rooms, F&B, Events and Spa. Strong occupancy — but a commercial dashboard that only watched rooms.",
    approach:
      "Audit the baseline department by department — RevPAR, TRevPAR, GOPPAR, utilisation, variable costs, forecast variance — then design the shift: KPIs, cadence, ownership, roadmap.",
    analysis: [
      "Departmental revenue composition — Rooms, F&B, Events, Spa",
      "RevPAR vs TRevPAR vs GOPPAR — what each KPI sees, and hides",
      "Variable cost and utilisation analysis across non-rooms departments",
      "Revenue capture and forecast variance — plan vs actual",
      "Organisational ownership of total revenue",
      "A phased implementation roadmap from rooms-first to total revenue",
    ],
    insightText:
      "Nearly 60% more revenue per available room sat outside the rooms-only metric.",
    recommendation: [
      "Forecast across departments, not rooms alone",
      "Evaluate decisions on total value and profit",
      "Create shared commercial ownership",
    ],
    demonstrates: ["Commercial Analysis", "Revenue Strategy", "Profitability", "Implementation Thinking"],
    nextSteps: [
      "Validate departmental demand data",
      "Build contribution-based displacement rules",
      "Pilot a shared forecasting cadence",
    ],
    takeaway:
      "Revenue optimisation means more when decisions are evaluated on total customer value and profitability, not rooms revenue alone.",
  },
  {
    slug: "fnb-performance-analysis",
    num: "03",
    category: "Hotel Performance Analysis",
    label: "Operational Performance Analysis — MSc Strategic Hotel Management",
    title: "When Revenue Grows but Profitability Doesn't",
    cardTitle: "When Revenue Grows but Profitability Doesn't",
    cardSub: "Comparative F&B performance analysis",
    questionLine: "Why can revenue grow while profitability falls?",
    home: {
      question: "Why can revenue grow while profitability falls?",
      context: "Comparative F&B performance analysis",
      tags: ["Margin", "Labour", "Covers", "Cost Structure"],
      outcome: "Labour alignment, not demand alone, drove the loss-making periods.",
      cta: "Explore the Diagnosis",
    },
    brief: {
      question: "If revenue increased, why did profitability collapse?",
      evidence:
        "Four quarters of departmental data for two simulated concepts — EASE (limited-service) and LUM (luxury full-service): revenue, margin, payroll, covers, average check and forecast variance.",
      insight:
        "Both concepts lost money in their strongest revenue quarter. Labour had scaled with optimism, not demand.",
      decision:
        "Align staffing to demand curves and manage margin — not just revenue — as the primary departmental KPI.",
    },
    insight:
      "Both concepts lost money in their strongest revenue quarter — labour had scaled with optimism, not demand.",
    tags: ["F&B Analysis", "Labour Productivity", "Forecast Variance"],
    metric: { value: "Q3", label: "peak revenue, negative margin — in both hotels" },
    image: IMAGES.restaurant,
    imageAlt: "Hotel restaurant service environment — the F&B operation under analysis",
    heroImage: U("photo-1414235077428-338989a2e8c0", "&h=900"),
    heroAlt: "Restaurant floor during service — where labour and demand meet",
    gallery: [
      { src: IMAGES.service, alt: "Table setting in a hotel restaurant", caption: "Service environment — illustrative" },
    ],
    context:
      "Two simulated concepts — EASE (limited-service) and LUM (luxury full-service) — tracked across four quarters of revenue, payroll, covers and cost data.",
    approach:
      "Read the concepts against each other, quarter by quarter, asking one question of every line: does this cost move with demand, or against it?",
    analysis: [
      "Quarterly departmental revenue and income for both concepts",
      "F&B margin against cost of sales, payroll and other expenses",
      "Covers and average check — the demand behind the revenue line",
      "Customer mix and its effect on spend per cover",
      "Staffing alignment — payroll committed vs covers delivered",
      "Forecast variance — where the plan and the quarter disagreed",
    ],
    insightText:
      "Revenue peaked in the same quarter profitability failed. Labour had been committed ahead of demand, exposing the margin when actual demand arrived differently from forecast.",
    recommendation: [
      "Align staffing to demand curves",
      "Review labour productivity weekly",
      "Manage margin alongside revenue",
    ],
    demonstrates: ["Performance Diagnosis", "Cost Analysis", "Labour Productivity", "Operational Decision-Making"],
    nextSteps: [
      "Test service-period labour models",
      "Validate demand drivers against actual staffing",
      "Extend profitability analysis by customer segment",
    ],
    takeaway:
      "Revenue growth alone is not operational health — labour productivity, cost structure and demand alignment decide whether revenue becomes profit.",
  },
  {
    slug: "hilton-brand-portfolio",
    num: "04",
    category: "Brand Strategy & Positioning",
    label: "MSc Strategic Analysis — Brand Strategy & Positioning",
    title: "Hilton Brand Portfolio & Positioning Strategy",
    cardTitle: "Hilton Brand Portfolio & Positioning Strategy",
    cardSub: "Segmentation, targeting and competitive positioning",
    questionLine: "How does a large hotel portfolio stay differentiated?",
    home: {
      question: "How does a large hotel portfolio stay differentiated?",
      context: "Segmentation, targeting and competitive positioning",
      tags: ["STP", "Portfolio Strategy", "Competitive Benchmarking"],
      outcome: "Portfolio breadth creates reach, but overlapping brands can weaken positioning clarity.",
      cta: "Explore the Positioning",
    },
    brief: {
      question: "How does a large hotel portfolio stay differentiated?",
      evidence:
        "An STP-led review of Hilton's brand architecture — segmentation, targeting and positioning from luxury to economy, benchmarked against competing global portfolios.",
      insight:
        "Portfolio breadth creates reach, but adjacent brands can weaken positioning clarity.",
      decision:
        "Sharpen positioning boundaries so each brand owns a distinct guest, promise and role.",
    },
    insight:
      "Portfolio breadth creates reach, but overlapping brands can weaken positioning clarity.",
    tags: ["STP", "Portfolio Strategy", "Competitive Benchmarking"],
    metric: { value: "STP", label: "segmentation · targeting · positioning" },
    image: IMAGES.lobbyAlt,
    imageAlt: "Premium hotel interior — the physical expression of a hotel brand portfolio",
    heroImage: U("photo-1677129667171-92abd8740fa3", "&h=900"),
    heroAlt: "Hotel architecture and interior — where brand positioning becomes physical",
    gallery: [
      { src: U("photo-1585418694458-dc80a5c20294", "&h=700&crop=bottom"), alt: "Distinctive boutique hotel interior", caption: "Brand environment — illustrative" },
    ],
    context:
      "Hilton operates one of the broadest brand architectures in hospitality, spanning luxury to economy. The analysis treated the portfolio as a system: each brand a position, each position a claim on a guest segment.",
    approach:
      "An STP lens — segmentation, targeting, positioning. Map the guests, assess which brand wins each one, then test every promise against its siblings and competing portfolios.",
    analysis: [
      "Guest segmentation — the needs, occasions and price bands the portfolio must cover",
      "Targeting logic — which brand is built to win which segment",
      "Positioning mapping — each brand's promise relative to its siblings",
      "Competitive benchmarking against other global hotel portfolios",
      "Overlap analysis — where adjacent brands compete for the same guest",
      "Portfolio coherence — where breadth extends reach, and where it dilutes clarity",
    ],
    insightText:
      "Portfolio breadth creates reach, but adjacent brands can weaken positioning clarity.",
    recommendation: [
      "Define each brand's single ownable guest promise",
      "Resolve overlaps in the crowded midscale and upscale tiers",
      "Gate future brand growth against unserved segments",
    ],
    demonstrates: ["STP", "Portfolio Strategy", "Competitive Benchmarking", "Brand Positioning"],
    nextSteps: [
      "Test brand perceptions with primary customer research",
      "Quantify overlap between adjacent brands",
      "Compare portfolio clarity across competitors",
    ],
    takeaway:
      "Brand strategy is portfolio economics: every brand must earn a distinct position, or it spends the clarity the others build.",
  },
];

export const ARCHIVE = [
  { title: "Revenue Management Simulation", note: "Pricing and inventory decisions under competitive pressure" },
  { title: "Hyatt vs Four Seasons — Hospitality Information Systems", note: "How two brands wire technology into guest experience" },
  { title: "Pan Pacific / UOL — Strategic Consultancy Exercise", note: "Group-level strategy for a hotel ownership platform" },
  { title: "Shangri-La — Strategic HRM Analysis", note: "People strategy as a service-quality engine" },
  { title: "Hospitality Consumer Research", note: "Evidence on how guests actually choose" },
  { title: "Business Plan & Entrepreneurship Projects", note: "Hospitality concepts taken from idea to plan" },
];
